<template>
  <div>
    <v-tooltip bottom>
      <template v-slot:activator="{ on, attrs }">
        <v-hover v-slot="{ hover }">
          <v-btn
            :color="hover ? 'error' : ''"
            v-bind="attrs"
            v-on="on"
            v-if="userHasPrivilege(['Send Delinquency Notification'])"
            @click="openModal"
            class="mx-1"
            icon
          >
            <v-icon>mdi-bug-outline</v-icon>
          </v-btn>
        </v-hover>
      </template>
      <span>Report an Issue</span>
    </v-tooltip>

    <!-- Modal for editing table value -->
    <ModalFormLayout
      @submit="onSubmit"
      @cancel="onClose"
      ref="reportIssueModal"
      :options="modalOptions"
      :loading="loading"
    >
      <template #header>
        <v-card-title>{{ modalOptions.title }}</v-card-title>
        <v-card-subtitle>{{ modalOptions.subtitle }}</v-card-subtitle>
        <v-divider />
      </template>
      <template #content>
        <ValidationObserver ref="reportIssueObserver">
          <v-row class="mt-2" align="start" dense>
            <!-- Module Name Field -->
            <v-col cols="12">
              <v-text-field
                dense
                outlined
                label="Module Name"
                v-model="issue.moduleName"
                disabled
              />
            </v-col>

            <!-- Description Field -->
            <v-col cols="12">
              <ValidationProvider
                name="Description"
                rules="max:255|required|min:5"
                v-slot="{ errors }"
              >
                <v-textarea
                  dense
                  counter="255"
                  outlined
                  label="Description*"
                  :error-messages="errors"
                  v-model="issue.description"
                  rows="5"
                  placeholder="Please describe the issue you are having in as much details as possible"
                  persistent-placeholder
                ></v-textarea>
              </ValidationProvider>
            </v-col>
            <v-col cols="12">
              <p class="font-weight-semibold text-subtitle-1 mb-0">Add Image</p>
              <span class="text--secondary">
                Optionally, you can add an image to help us understand the issue
                better
              </span>
              <br />
              <v-btn
                depressed
                outlined
                color="primary"
                class="text-capitalize mt-4"
                @click="open()"
              >
                <v-icon left small> mdi-cloud-upload-outline </v-icon>
                {{ selectedFile ? 'Change' : 'Upload' }} Image</v-btn
              >
              <br />
              <v-img
                v-if="imageUrl"
                :src="imageUrl"
                class="mt-4 rounded-lg"
                width="250"
                height="250"
              ></v-img>
              <v-btn
                depressed
                @click="clear()"
                class="text-capitalize mt-4"
                small
                outlined
                v-if="imageUrl"
              >
                <v-icon left small>mdi-delete</v-icon>
                Remove Image
              </v-btn>
            </v-col>
          </v-row>
        </ValidationObserver>
      </template>
    </ModalFormLayout>
  </div>
</template>

<script>
import PageMixin from '@/mixins/page.mixin';
import { ReportIssueService } from '../../services/report-issue.service';
import { mapGetters } from 'vuex';
import { responseErrorHelper } from '@/lib/http';
import { useFileDialog, useBase64 } from '@vueuse/core';
import { ref, getCurrentInstance } from 'vue';
import accessControlMixin from '@/mixins/accessControl.mixin';
export default {
  name: 'ReportIssueDialog',
  mixins: [PageMixin,accessControlMixin],
  async created() {
    // Initialize report issue service
    this.reportIssueService = new ReportIssueService();
    const path = window.location.pathname;
    const moduleName = path.split('/')[1] || '';
    // Set default values
    this.issue.moduleName = moduleName;
    this.issue.userId = this.user.id;
    this.issue.companyName = this.currentCompany.name;
    this.issue.status = 'Pending';
    this.issue.additionalDetails = {
      user: this.user?.username,
      company: this.currentCompany?.name,
      userEmail: this.user?.email,
      reportedFrom: window.location.href,
      url: ''
    };
  },
  setup() {
    const instance = getCurrentInstance();
    const selectedFile = ref(null);
    const { base64: imageUrl } = useBase64(selectedFile);
    const { open, onChange, reset } = useFileDialog({
      reset: true,
      accept: 'image/*',
      multiple: false
    });
    onChange((files) => {
      if (!files) return;
      const file = files[0];
      if (!file) return;
      if (!file.type.match('image/')) {
        return instance.proxy.showSnack({
          color: 'error',
          title: 'Invalid File',
          message: 'Please select an image file'
        });
      }
      if (file.size > instance.proxy.maxSize) {
        return instance.proxy.showSnack({
          color: 'error',
          title: 'File too large',
          message: 'Please select an image file under 3MB'
        });
      }
      selectedFile.value = file;
    });
    // Function used to clear the selected file
    const clear = () => {
      selectedFile.value = null;
      reset();
    };

    return { open, onChange, selectedFile, imageUrl, clear };
  },
  data() {
    return {
      loading: false,
      modal: false,
      errorDialog: false,
      errorText: '',
      // Modal options
      modalOptions: {
        title: 'Report Issue',
        subtitle: 'Enter the issue and click submit',
        submitButtonText: 'Submit',
        maxWidth: '800px',
        width: '800px',
        color: 'white',
        flat: true
      },
      issue: {},
      maxSize: 3000000,
      reportIssueService: null
    };
  },

  computed: {
    ...mapGetters('authModule', ['user']),
    ...mapGetters('companyModule', ['currentCompany'])
  },

  methods: {
    openModal() {
      this.$refs.reportIssueModal.open();
    },

    async onSubmit() {
      this.loading = true;
      try {
        const isValid = await this.$refs.reportIssueObserver.validate();
        if (!isValid) return;
        // Create the Issue
        const response = await this.reportIssueService.create(this.issue);
        // Upload the image if it exists
        if (this.selectedFile) await this.uploadFile(response.data.id);
        this.showSnack({
          color: 'success',
          title: `Issue Submitted`,
          message: `Issue has been reported successfully`
        });
        // Close the modal
        this.clear();
        this.onClose();
      } catch (error) {
        const err = responseErrorHelper(error);
        this.showSnack({
          color: 'error',
          title: `Could not submit issue`,
          message: err
        });
      } finally {
        this.loading = false;
      }
    },

    onClose() {
      this.$refs.reportIssueObserver?.reset();
      this.issue.description = '';
      this.selectedFile = null;
      this.$forceUpdate();
      //Modal will not close without adding a delay
      setTimeout(() => {
        this.$refs.reportIssueModal?.close();
      }, 300);
    },

    async uploadFile(reportId) {
      try {
        await this.reportIssueService.uploadImage(reportId, this.selectedFile);
      } catch (error) {
        const err = responseErrorHelper(error);
        this.showSnack({
          color: 'error',
          title: `Could not upload image`,
          message: err
        });
      }
    }
  }
};
</script>
